import InfoIcon from "@mui/icons-material/Info";
import LoadingEmptyError from "components/LoaderEmptyErrorState";
import {
  getSurveyListPaginationApi,
  recordSurveyFailure,
  surveyIncreaseClicks,
  surveyUpdate,
} from "data/slices/surveylist/api";
import { IncreaseClickResponse } from "data/slices/surveylist/interface";
import keys from "locale/keys";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  DISQUALIFIED_REJECTION_IDS,
  ROUTES,
  STORAGE_USER_ID,
  SURVEY_STATUS,
  SURVEY_STATUS_ASSIGNED,
} from "utils/constants";
import { surveysMapper } from "utils/mappers/SurveysListMapper";
import { getFromLocalStorage, saveToLocalStorage } from "utils/Utils";

import AppLogo from "../../assets/icons/ApplogoBlack.svg";
import Button from "../../components/button/index";
import DisqualifiedRewardDialog from "../../components/disqualifiedrewarddialog";
import { SuggestedSurvey } from "../../components/disqualifiedrewarddialog/interface";
import { AppLogoImg, Message, Wrapper } from "./styled.components";
import {
  getDynataStatusMessage,
  getFailurePayload,
  getRequestPayload,
  getTolunaStatusMessage,
  isLucidMeasurement,
  isSurveySucccess,
  isToluna,
  TolunaRoutes,
} from "./utils";

//these are specific to dynata
const SurveyInfoRedirection = () => {
  const [params] = useSearchParams();
  const { pathname, search, hash } = useLocation();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [showRewardPopup, setShowRewardPopup] = useState(false);
  const [suggestedSurvey, setSuggestedSurvey] =
    useState<SuggestedSurvey | null>(null);
  const [isFetchingSuggestedSurvey, setIsFetchingSuggestedSurvey] =
    useState(false);

  const rejectionID =
    params.get("rejectionID") || params.get("RespondentRejectionTypeID");
  const isDisqualifiedRewardEligible =
    pathname === ROUTES.TOLUNA_NOT_QUALIFIED &&
    DISQUALIFIED_REJECTION_IDS.indexOf(Number(rejectionID)) !== -1;

  useEffect(() => {
    submitSurveyStatus();
    if (isDisqualifiedRewardEligible) {
      setShowRewardPopup(true);
      fetchSuggestedSurvey();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitSurveyStatus = async () => {
    setIsLoading(true);

    await isSurveySucccess(pathname, params);

    const userId = getFromLocalStorage(STORAGE_USER_ID);
    const tolunaUrl = pathname + search + hash;
    const request = getRequestPayload(userId, pathname, params, tolunaUrl);
    if (request) {
      //explicitly set the status as failed
      request.status = SURVEY_STATUS.FAILED;
      if (!TolunaRoutes.includes(pathname)) {
        await surveyUpdate(request);
      }
      const failurePayload = getFailurePayload(
        userId,
        params,
        pathname,
        tolunaUrl,
      );
      if (failurePayload) {
        await recordSurveyFailure(failurePayload);
      }
    }

    setIsLoading(false);
  };

  const fetchSuggestedSurvey = async () => {
    setIsFetchingSuggestedSurvey(true);
    const data = await getSurveyListPaginationApi({
      status: SURVEY_STATUS_ASSIGNED,
      pageSize: 1,
      page: 1,
    });
    const rows = data?.response?.rows ?? [];
    const mapped = surveysMapper(rows).surveyItems;
    const first = mapped?.[0];
    setSuggestedSurvey(
      first
        ? {
            id: first.id,
            surveyNumber: String(first.surveyNumber),
            points: first.points,
            timeToComplete: first.timeToComplete,
            url: first.url,
            providerKey: first.providerKey,
          }
        : null,
    );
    setIsFetchingSuggestedSurvey(false);
  };

  const handleRewardPopupDismiss = () => {
    setShowRewardPopup(false);
  };

  const handleStartSuggestedSurvey = async (survey: SuggestedSurvey) => {
    const response: IncreaseClickResponse = await surveyIncreaseClicks(
      survey.id,
    );
    if (response?.link) {
      window.open(response.link, "_blank", "noopener,noreferrer");
    }
    setShowRewardPopup(false);
  };

  const handleStartSurvey = () => {
    saveToLocalStorage("reload_surveys", Date.now().toString());
    setTimeout(() => {
      window.close();
    }, 100);
  };

  const getStatusMessage = () => {
    if (isLucidMeasurement(params)) {
      return getDynataStatusMessage(
        "",
        "",
        params.get("ProofStatus") ?? undefined,
        params.get("ProofStatus") ?? undefined,
      );
    }
    if (isToluna(pathname)) {
      // Here we are check is Toluna redirection or not.
      const message = getTolunaStatusMessage(pathname, params);
      return message;
    }
    return getDynataStatusMessage(
      params.get("disposition")?.toLowerCase() ?? "",
      params.get("status")?.toLowerCase() ?? "",
      params.get("marketplace_status") ?? undefined,
      params.get("client_status") ?? undefined,
    );
  };

  return (
    <Wrapper>
      <AppLogoImg src={AppLogo} alt="app logo" />
      <InfoIcon style={{ color: "gray", width: "4rem", height: "4rem" }} />
      <Message>{getStatusMessage()}</Message>
      {!isLoading && (
        <Button onClickHandler={handleStartSurvey}>
          {t(keys.go_to_surveys)}
        </Button>
      )}
      {isLoading && (
        <div>
          <LoadingEmptyError isLoading={true} isError={false} isEmpty={false} />
        </div>
      )}
      {isDisqualifiedRewardEligible && (
        <DisqualifiedRewardDialog
          show={showRewardPopup}
          dismiss={handleRewardPopupDismiss}
          survey={suggestedSurvey}
          isLoadingSurvey={isFetchingSuggestedSurvey}
          onStartSurveyClicked={handleStartSuggestedSurvey}
        />
      )}
    </Wrapper>
  );
};

export default SurveyInfoRedirection;
